/**
 * Findings views (contracts/ui-api.md "Results (sheets 20, 21)"; mission 5
 * results-api.md; C-006, C-010, NFR-005). Every page is requested with
 * `limit=50`, except the one `limit=1` read that supplies `rule_counts`, which
 * the by-rule body lacks (post-plan C3).
 */
import { api, idSegment, oneOf, validRuleId } from '../api'
import type { Severity } from '../severity'

export type { Severity }
const SEVERITIES: readonly Severity[] = ['ERROR', 'WARN', 'INFO']

export interface SeverityCounts {
  ERROR: number
  WARN: number
  INFO: number
}

/** A (rule, severity) segment past the 500-per-rule window (DM-01M39PR43C8WYP2S7T8VYTYTZE). */
export interface TruncatedRule {
  rule_id: string
  severity: Severity
  count: number
  in_window: number
  listed_through_line: number
}

export interface ByRuleEntry {
  rule_id: string
  severity: Severity
  description: string | null
  segment: string | null
  field: string | null
  count: number
  in_window: number
  truncated: boolean
  explanation_status: string | null
}

/** GET /v1/runs/{id}/findings/by-rule. */
export interface ByRulePage {
  rule_set_version: string
  counts: SeverityCounts
  truncated_rules: TruncatedRule[]
  severity_counts_exact: boolean
  findings_truncated: boolean
  entries: ByRuleEntry[]
  next_cursor?: string
}

export interface ByRecordEntry {
  line: number
  record_ref: string | null
  counts: SeverityCounts
  rule_ids: string[]
  complete: boolean
}

/** GET /v1/runs/{id}/findings/by-record. */
export interface ByRecordPage {
  rule_set_version: string
  complete: boolean
  complete_before_line: number | null
  file_level: SeverityCounts
  truncated_rules: TruncatedRule[]
  entries: ByRecordEntry[]
  next_cursor?: string
}

/** available, withheld or not-located (DM-01M39PR6WCD3792M8TVM59WYJ0). */
export type ValueState = 'available' | 'withheld' | 'not-located' | (string & {})

export interface FindingEntry {
  rule_id: string
  severity: Severity
  segment: string | null
  field: string | null
  line: number
  record_ref: string | null
  /** Render only when `value_state === 'available'` (FR-013, TEN.10). */
  flagged_value: string | null
  value_state: ValueState
}

/** GET /v1/runs/{id}/findings. */
export interface FindingsPage {
  rule_set_version: string
  counts: SeverityCounts
  /** Per-rule totals: the footer's M and the `?rule=` fallback (C3, C16). */
  rule_counts: Record<string, number>
  truncated_rules: TruncatedRule[]
  entries: FindingEntry[]
  next_cursor?: string
}

export interface TrailerStatistic {
  name: string
  declared: number
  calculated: number
  consistent: boolean
}

/** GET /v1/runs/{id}/trailer. */
export interface Trailer {
  rule_set_version: string
  consistent: boolean | null
  statistics: TrailerStatistic[]
}

export interface ByRuleFilters {
  severity?: Severity
  cursor?: string
}

export interface ByRecordFilters {
  cursor?: string
}

export interface FindingFilters {
  rule_id?: string
  severity?: Severity
  field?: string
  segment?: string
  cursor?: string
}

function runPath(id: string, view: string): string {
  return `/runs/${idSegment(id, 'run id')}${view}`
}

/** `?limit=50` plus the given filters, in a fixed order, each validated (S6). */
function query(filters: FindingFilters): string {
  const q = new URLSearchParams({ limit: '50' })
  if (filters.rule_id !== undefined) q.set('rule_id', validRuleId(filters.rule_id))
  if (filters.severity !== undefined) q.set('severity', oneOf(filters.severity, SEVERITIES, 'severity'))
  if (filters.field !== undefined) q.set('field', filters.field)
  if (filters.segment !== undefined) q.set('segment', filters.segment)
  if (filters.cursor !== undefined) q.set('cursor', filters.cursor)
  return q.toString()
}

/** `rule_counts` for a ready run: one finding page of one row (C3). */
export async function getRuleCounts(id: string): Promise<FindingsPage> {
  return api<FindingsPage>(`${runPath(id, '/findings')}?limit=1`)
}

export async function getByRule(id: string, filters: ByRuleFilters): Promise<ByRulePage> {
  const path = runPath(id, '/findings/by-rule')
  const f: FindingFilters = {}
  if (filters.severity !== undefined) f.severity = filters.severity
  if (filters.cursor !== undefined) f.cursor = filters.cursor
  return api<ByRulePage>(`${path}?${query(f)}`)
}

export async function getByRecord(id: string, filters: ByRecordFilters): Promise<ByRecordPage> {
  const path = runPath(id, '/findings/by-record')
  const f: FindingFilters = {}
  if (filters.cursor !== undefined) f.cursor = filters.cursor
  return api<ByRecordPage>(`${path}?${query(f)}`)
}

export async function getFindings(id: string, filters: FindingFilters): Promise<FindingsPage> {
  const path = runPath(id, '/findings')
  const f: FindingFilters = {}
  if (filters.rule_id !== undefined) f.rule_id = filters.rule_id
  if (filters.severity !== undefined) f.severity = filters.severity
  if (filters.field !== undefined) f.field = filters.field
  if (filters.segment !== undefined) f.segment = filters.segment
  if (filters.cursor !== undefined) f.cursor = filters.cursor
  return api<FindingsPage>(`${path}?${query(f)}`)
}

export async function getTrailer(id: string): Promise<Trailer> {
  return api<Trailer>(runPath(id, '/trailer'))
}
